import { createPortal } from 'react-dom';
import { ExclamationmarkSmallTriangleFillIcon, XIcon } from '@/icons';
import { Button } from '@/ui_v3';
import { cn } from '@/utils';
type AlertBannerType = 'warning' | 'success' | 'info' | 'error';
interface AlertBannerProps {
  title: string;
  onClose: () => void;
  datatype?: string;
  type?: AlertBannerType;
  rightSlot?: React.ReactNode;
}
const BANNER_STYLES: Record<AlertBannerType, {
  bg: string;
  icon: React.ReactNode;
  iconColor: string;
}> = {
  warning: {
    bg: 'bg-[rgba(255,255,255,0.90)]',
    icon: <ExclamationmarkSmallTriangleFillIcon fill="#F4C620" className="flex shrink-0" size="md" />,
    iconColor: '#F4C620'
  },
  success: {
    bg: 'bg-[rgba(242,255,247,0.90)]',
    icon: <ExclamationmarkSmallTriangleFillIcon fill="#4CAF50" className="flex shrink-0" size="md" />,
    iconColor: '#4CAF50'
  },
  info: {
    bg: 'bg-[rgba(242,250,255,0.90)]',
    icon: <ExclamationmarkSmallTriangleFillIcon fill="#2196F3" className="flex shrink-0" size="md" />,
    iconColor: '#2196F3'
  },
  error: {
    bg: 'bg-[rgba(255,242,242,0.90)]',
    icon: <ExclamationmarkSmallTriangleFillIcon fill="#F44336" className="flex shrink-0" size="md" />,
    iconColor: '#F44336'
  }
};

/**
 * 재사용 가능한 알림 배너 UI 컴포넌트
 *
 * TextNotice와 PlanExpirationAlertBanner에서 공통으로 사용합니다.
 * createPortal을 통해 document.body에 마운트되어 z-index 겹침을 방지합니다.
 *
 * @param title - 알림 메시지 제목
 * @param onClose - 닫기 버튼 클릭 핸들러
 * @param datatype - data-type 속성값 (기본값: 'alert')
 * @param type - 배너 타입 (warning, success, info, error) - 기본값: 'warning'
 * @param rightSlot - 오른쪽 슬롯에 렌더링할 ReactNode (버튼, 링크 등)
 */
export const AlertBanner = ({
  title,
  onClose,
  datatype,
  type = 'warning',
  rightSlot
}: AlertBannerProps) => {
  const bannerStyle = BANNER_STYLES[type];
  return createPortal(<div datatype={datatype || 'alert'} className={cn(['min-w-[calc(100vw-32px)] tablet-sm:min-w-[300px]', 'animate-duration-500 animate-delay-[400ms] animate-ease-in', 'fixed top-[100px] items-center p-sm flex justify-between', '-translate-x-1/2 left-1/2 z-notice flex max-w-[1000px] gap-xs rounded-sm', bannerStyle.bg, 'backdrop-blur-[2px] shadow-[0px_4px_12px_0px_rgba(28,27,31,0.12),_0px_0px_16px_0px_rgba(28,27,31,0.04)]', 'pointer-events-auto'])}>
      <div className="flex shrink items-center gap-xs">
        {bannerStyle.icon}
        <span className="text-body-lg-semibold text-default">{title}</span>
      </div>
      <div className="flex shrink-0 items-center gap-xs">
        {rightSlot}
        <Button intent="neutral" size="sm" className="px-2xs" onClick={onClose} variant="transparent">
          <XIcon fill="#5D5E69" />
        </Button>
      </div>
    </div>, document.body);
};